/* ==========================================================================
   Ella Luxe — theme scripts (no dependencies)
   ========================================================================== */
(() => {
  const Ella = (window.Ella = window.Ella || {});
  Ella.settings = Ella.settings || {};
  Ella.strings = Ella.strings || {};
  Ella.routes = Ella.routes || {};

  const isRTL = () => document.documentElement.dir === 'rtl';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Utilities ---------- */
  Ella.debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  Ella.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  Ella.formatMoney = (cents, format = Ella.settings.moneyFormat || '{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (num, precision, thousands = ',', decimal = '.') => {
      if (num == null || isNaN(num)) return '0';
      const fixed = (num / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (frac ? decimal + frac : '');
    };
    const match = format.match(placeholder);
    if (!match) return format;
    const map = {
      amount: () => fmt(cents, 2),
      amount_no_decimals: () => fmt(cents, 0),
      amount_with_comma_separator: () => fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => fmt(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => fmt(cents, 0, ' ', ''),
      amount_with_space_separator: () => fmt(cents, 2, ' ', ','),
      amount_with_period_and_space_separator: () => fmt(cents, 2, ' ', '.'),
    };
    return format.replace(placeholder, (map[match[1]] || map.amount)());
  };

  Ella.toast = (message) => {
    const toast = document.getElementById('EllaToast');
    if (!toast || !message) return;
    toast.textContent = message;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => (toast.hidden = true), 500);
    }, 3200);
  };

  /* ---------- Scroll lock & focus ---------- */
  let scrollLocks = 0;
  Ella.lockScroll = () => {
    scrollLocks += 1;
    document.body.classList.add('scroll-locked');
  };
  Ella.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (!scrollLocks) document.body.classList.remove('scroll-locked');
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  Ella.trapFocus = (container) => {
    Ella.releaseFocus();
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const els = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    Ella._trapHandler = handler;
  };

  Ella.releaseFocus = () => {
    if (Ella._trapHandler) document.removeEventListener('keydown', Ella._trapHandler);
    Ella._trapHandler = null;
  };

  /* ---------- Overlays (drawers, modals, search, lightbox) ---------- */
  const overlayStack = [];
  const OVERLAY_SELECTOR = '.drawer, .modal, .search-overlay, .lightbox';

  Ella.open = (el, trigger) => {
    if (!el || el.classList.contains('is-open')) return;
    el._trigger = trigger || document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    Ella.lockScroll();
    overlayStack.push(el);
    if (el._trigger && el._trigger.hasAttribute && el._trigger.hasAttribute('aria-expanded')) {
      el._trigger.setAttribute('aria-expanded', 'true');
    }
    const panel = el.querySelector('[role="dialog"]') || el;
    Ella.trapFocus(panel);
    setTimeout(() => {
      const target = el.querySelector('[data-autofocus]') || panel;
      target.focus({ preventScroll: true });
    }, 120);
    el.dispatchEvent(new CustomEvent('ella:open'));
  };

  Ella.close = (el) => {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    Ella.unlockScroll();
    const idx = overlayStack.indexOf(el);
    if (idx > -1) overlayStack.splice(idx, 1);
    Ella.releaseFocus();
    const previous = overlayStack[overlayStack.length - 1];
    if (previous) Ella.trapFocus(previous.querySelector('[role="dialog"]') || previous);
    if (el._trigger && el._trigger.focus) {
      if (el._trigger.hasAttribute && el._trigger.hasAttribute('aria-expanded')) {
        el._trigger.setAttribute('aria-expanded', 'false');
      }
      el._trigger.focus({ preventScroll: true });
    }
    el.dispatchEvent(new CustomEvent('ella:close'));
  };

  Ella.closeAll = (except) => {
    [...overlayStack].reverse().forEach((el) => {
      if (el !== except) Ella.close(el);
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && overlayStack.length) Ella.close(overlayStack[overlayStack.length - 1]);
  });

  /* ---------- Reveal on scroll ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const target = entry.target;
              target.classList.add('is-revealed');
              revealObserver.unobserve(target);
              if (target.dataset.reveal === 'fade') setTimeout(() => target.classList.add('is-settled'), 2200);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
        )
      : null;

  Ella.reveal = (scope = document) => {
    const els = scope.querySelectorAll('[data-reveal]:not(.is-revealed)');
    const animate = document.body.classList.contains('anim-reveal') && !designMode && !reducedMotion && revealObserver;
    els.forEach((el) => (animate ? revealObserver.observe(el) : el.classList.add('is-revealed', 'is-settled')));
  };

  Ella.whenReady = (callback) => {
    if (document.documentElement.classList.contains('is-preloading')) {
      document.addEventListener('ella:preloaded', callback, { once: true });
    } else {
      callback();
    }
  };

  /* ---------- Parallax ---------- */
  const parallaxEls = new Set();
  let parallaxTicking = false;
  const parallaxTick = () => {
    if (parallaxTicking) return;
    parallaxTicking = true;
    requestAnimationFrame(() => {
      const vh = window.innerHeight;
      parallaxEls.forEach((el) => {
        if (!el.isConnected) {
          parallaxEls.delete(el);
          return;
        }
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const offset = rect.top + rect.height / 2 - vh / 2;
        const factor = parseFloat(el.dataset.parallax) || 0.1;
        el.style.transform = `translate3d(0, ${(-offset * factor).toFixed(1)}px, 0)`;
      });
      parallaxTicking = false;
    });
  };

  Ella.parallax = (scope = document) => {
    if (reducedMotion || window.innerWidth < 750) return;
    scope.querySelectorAll('[data-parallax]').forEach((el) => parallaxEls.add(el));
    parallaxTick();
  };
  window.addEventListener('scroll', parallaxTick, { passive: true });

  /* ---------- Split heading words ---------- */
  Ella.splitWords = (el) => {
    if (!el || el.dataset.splitDone) return;
    el.dataset.splitDone = 'true';
    let index = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const fragment = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              fragment.appendChild(document.createTextNode(part));
              return;
            }
            const outer = document.createElement('span');
            outer.className = 'word';
            const inner = document.createElement('span');
            inner.className = 'word__inner';
            inner.style.setProperty('--w', index++);
            inner.textContent = part;
            outer.appendChild(inner);
            fragment.appendChild(outer);
          });
          child.replaceWith(fragment);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
    el.classList.add('is-split');
  };

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      this.section = this.closest('.section-header') || this.parentElement;
      this.mode = this.dataset.sticky;
      this.transparent = this.hasAttribute('data-transparent');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      this.setHeight = this.setHeight.bind(this);

      this.setHeight();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.setHeight);
        this.resizeObserver.observe(this.header);
      }
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      if (this.transparent) {
        this.header.addEventListener('mouseenter', () => this.header.classList.add('is-solid'));
        this.header.addEventListener('mouseleave', () => this.onScroll());
        this.header.addEventListener('focusin', () => this.header.classList.add('is-solid'));
      }

      this.querySelectorAll('.nav__item--parent').forEach((item) => {
        const link = item.querySelector('.nav__link');
        const set = (value) => link && link.setAttribute('aria-expanded', value);
        item.addEventListener('mouseenter', () => set('true'));
        item.addEventListener('mouseleave', () => set('false'));
        item.addEventListener('focusin', () => set('true'));
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) set('false');
        });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    setHeight() {
      const height = this.header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      const bottom = this.header.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty('--header-bottom', `${Math.max(bottom, 0)}px`);
    }

    onScroll() {
      const y = window.scrollY;
      this.header.classList.toggle('is-scrolled', y > 10);
      if (this.transparent) this.header.classList.toggle('is-solid', y > 10);

      if (this.mode === 'scroll-up' && !overlayStack.length) {
        const goingDown = y > this.lastY;
        this.section.classList.toggle('is-hidden', goingDown && y > 400);
      }
      this.lastY = y;
      document.documentElement.style.setProperty(
        '--header-bottom',
        `${Math.max(this.header.getBoundingClientRect().bottom, 0)}px`
      );
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Simple overlay elements ---------- */
  class OverlayElement extends HTMLElement {
    open(trigger) {
      Ella.open(this, trigger);
    }
    close() {
      Ella.close(this);
    }
  }
  customElements.define('menu-drawer', class extends OverlayElement {});
  customElements.define('ella-modal', class extends OverlayElement {});

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      if (this.items.length < 2) return;
      this.index = 0;
      this.speed = Number(this.dataset.speed) || 5000;
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.start();
    }
    disconnectedCallback() {
      this.stop();
    }
    go(i) {
      const count = this.items.length;
      this.items[this.index].classList.remove('is-active');
      this.items[this.index].setAttribute('aria-hidden', 'true');
      this.index = (i + count) % count;
      this.items[this.index].classList.add('is-active');
      this.items[this.index].removeAttribute('aria-hidden');
      this.start();
    }
    start() {
      this.stop();
      if (!designMode) this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
    stop() {
      clearInterval(this.timer);
    }
    selectBlock(block) {
      const i = this.items.indexOf(block);
      if (i > -1) this.go(i);
      this.stop();
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Hero slideshow ---------- */
  class EllaSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.hero__slide')];
      this.bars = [...this.querySelectorAll('.hero__bar')];
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.speed = Number(this.dataset.speed) || 6000;

      this.querySelectorAll('[data-split]').forEach(Ella.splitWords);

      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.bars.forEach((bar) => bar.addEventListener('click', () => this.go(Number(bar.dataset.go))));

      this.addEventListener('pointerdown', (event) => {
        this.startX = event.clientX;
      });
      this.addEventListener('pointerup', (event) => {
        if (this.startX == null) return;
        const dx = event.clientX - this.startX;
        this.startX = null;
        if (Math.abs(dx) < 50) return;
        const forward = isRTL() ? dx > 0 : dx < 0;
        this.go(this.index + (forward ? 1 : -1));
      });
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());

      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.play() : this.pause()));
        this.visibility.observe(this);
      }

      Ella.whenReady(() => {
        const first = this.slides[0];
        if (first) requestAnimationFrame(() => first.classList.add('is-ready'));
        this.play();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.visibility) this.visibility.disconnect();
    }

    go(target) {
      const count = this.slides.length;
      if (count < 2) return;
      const nextIndex = (target + count) % count;
      if (nextIndex === this.index) return;
      const current = this.slides[this.index];
      const incoming = this.slides[nextIndex];

      current.classList.remove('is-active', 'is-ready', 'is-entering');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');

      incoming.classList.remove('is-leaving');
      incoming.classList.add('is-active', 'is-entering');
      incoming.removeAttribute('aria-hidden');
      void incoming.offsetWidth;
      requestAnimationFrame(() => incoming.classList.add('is-ready'));

      const video = incoming.querySelector('video');
      if (video) video.play().catch(() => {});

      clearTimeout(this.cleanupTimer);
      this.cleanupTimer = setTimeout(() => {
        current.classList.remove('is-leaving');
        incoming.classList.remove('is-entering');
      }, 1600);

      this.bars.forEach((bar, i) => {
        bar.classList.remove('is-active');
        bar.setAttribute('aria-selected', i === nextIndex ? 'true' : 'false');
      });
      const bar = this.bars[nextIndex];
      if (bar) {
        void bar.offsetWidth;
        bar.classList.add('is-active');
      }

      this.index = nextIndex;
      this.play();
    }

    play() {
      clearTimeout(this.timer);
      this.classList.remove('is-paused');
      if (!this.autoplay || this.paused) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.speed);
    }

    pause() {
      clearTimeout(this.timer);
      this.classList.add('is-paused');
    }

    selectBlock(block) {
      const i = this.slides.indexOf(block);
      this.paused = true;
      if (i > -1) this.go(i);
      this.slides[i] && this.slides[i].classList.add('is-ready');
      this.pause();
    }

    deselectBlock() {
      this.paused = false;
      this.play();
    }
  }
  customElements.define('ella-slideshow', EllaSlideshow);

  /* ---------- Scroll-snap slider ---------- */
  class EllaSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.progress = this.querySelector('[data-slider-progress]');
      this.update = this.update.bind(this);

      if (this.prev) this.prev.addEventListener('click', () => this.move(-1));
      if (this.next) this.next.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', () => requestAnimationFrame(this.update), { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    move(direction) {
      const amount = this.track.clientWidth * 0.85 * direction * (isRTL() ? -1 : 1);
      this.track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const pos = Math.abs(this.track.scrollLeft);
      this.classList.toggle('is-static', max <= 4);
      if (this.prev) this.prev.disabled = pos <= 4;
      if (this.next) this.next.disabled = pos >= max - 4;
      if (this.progress && this.track.scrollWidth) {
        const width = (this.track.clientWidth / this.track.scrollWidth) * 100;
        this.progress.style.width = `${width}%`;
        this.progress.style.insetInlineStart = `${(pos / this.track.scrollWidth) * 100}%`;
      }
    }
  }
  customElements.define('ella-slider', EllaSlider);

  /* ---------- Tabs ---------- */
  class EllaTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => {
          const keys = isRTL() ? ['ArrowLeft', 'ArrowRight'] : ['ArrowRight', 'ArrowLeft'];
          const i = this.tabs.indexOf(tab);
          if (event.key === keys[0]) this.focusTab(i + 1);
          if (event.key === keys[1]) this.focusTab(i - 1);
        });
      });
    }
    focusTab(i) {
      const tab = this.tabs[(i + this.tabs.length) % this.tabs.length];
      tab.focus();
      this.select(tab);
    }
    select(tab) {
      this.tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (active) {
          const slider = panel.querySelector('ella-slider');
          if (slider && slider.update) slider.update();
          Ella.reveal(panel);
        }
      });
    }
    selectBlock(block) {
      if (block.getAttribute('role') === 'tab') this.select(block);
    }
  }
  customElements.define('ella-tabs', EllaTabs);

  /* ---------- Fader (testimonials) ---------- */
  class EllaFader extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.testimonial')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.index = 0;
      this.speed = Number(this.dataset.speed) || 7000;
      this.autoplay = this.dataset.autoplay === 'true' && this.items.length > 1;
      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go))));
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.start());
      this.start();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    go(i) {
      const count = this.items.length;
      if (!count) return;
      this.items[this.index].classList.remove('is-active');
      this.items[this.index].setAttribute('aria-hidden', 'true');
      if (this.dots[this.index]) this.dots[this.index].classList.remove('is-active');
      this.index = (i + count) % count;
      this.items[this.index].classList.add('is-active');
      this.items[this.index].removeAttribute('aria-hidden');
      if (this.dots[this.index]) this.dots[this.index].classList.add('is-active');
      this.start();
    }
    start() {
      clearInterval(this.timer);
      if (this.autoplay && !designMode) this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
    selectBlock(block) {
      const i = this.items.indexOf(block);
      if (i > -1) this.go(i);
      clearInterval(this.timer);
    }
  }
  customElements.define('ella-fader', EllaFader);

  /* ---------- Lookbook hotspots ---------- */
  class LookBook extends HTMLElement {
    connectedCallback() {
      this.hotspots = [...this.querySelectorAll('.hotspot')];
      this.hotspots.forEach((spot) => {
        spot.querySelector('.hotspot__dot').addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggle(spot);
        });
      });
      this.onDocClick = (event) => {
        if (!this.contains(event.target)) this.toggle(null);
      };
      document.addEventListener('click', this.onDocClick);
      const section = this.closest('.lookbook');
      if (section) {
        section.querySelectorAll('[data-hotspot-target]').forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.stopPropagation();
            const spot = this.querySelector(`#Hotspot-${btn.dataset.hotspotTarget}`);
            this.toggle(spot, true);
            section.querySelectorAll('[data-hotspot-target]').forEach((b) => b.classList.toggle('is-active', b === btn));
            if (window.innerWidth < 990) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        });
      }
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }
    toggle(spot, forceOpen = false) {
      this.hotspots.forEach((s) => {
        const open = s === spot && (forceOpen || !s.classList.contains('is-open'));
        s.classList.toggle('is-open', open);
        const dot = s.querySelector('.hotspot__dot');
        if (dot) dot.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    selectBlock(block) {
      this.toggle(block, true);
    }
  }
  customElements.define('look-book', LookBook);

  /* ---------- Countdown ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const raw = this.dataset.end || '';
      const [datePart, timePart = '23:59:00'] = raw.split('T');
      const [y, m, d] = datePart.split('-').map(Number);
      const [hh, mm, ss] = timePart.split(':').map(Number);
      this.end = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0).getTime();
      if (isNaN(this.end)) return;
      this.els = {
        days: this.querySelector('[data-days]'),
        hours: this.querySelector('[data-hours]'),
        minutes: this.querySelector('[data-minutes]'),
        seconds: this.querySelector('[data-seconds]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    tick() {
      const diff = Math.max(0, this.end - Date.now());
      const pad = (n) => String(n).padStart(2, '0');
      this.els.days.textContent = pad(Math.floor(diff / 86400000));
      this.els.hours.textContent = pad(Math.floor((diff / 3600000) % 24));
      this.els.minutes.textContent = pad(Math.floor((diff / 60000) % 60));
      this.els.seconds.textContent = pad(Math.floor((diff / 1000) % 60));
      if (diff === 0) {
        clearInterval(this.timer);
        const ended = this.parentElement.querySelector('.countdown__ended');
        if (ended) ended.hidden = false;
        if (this.dataset.hideEnded === 'true') this.hidden = true;
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Video lightbox button ---------- */
  class VideoModalButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (button) button.addEventListener('click', () => this.openVideo(button));
    }
    openVideo(trigger) {
      const id = this.dataset.videoId;
      const src =
        this.dataset.videoType === 'vimeo'
          ? `https://player.vimeo.com/video/${id}?autoplay=1`
          : `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
      const modal = document.createElement('div');
      modal.className = 'modal video-modal';
      modal.setAttribute('aria-hidden', 'true');
      modal.innerHTML = `
        <div class="modal__overlay" data-close></div>
        <div class="modal__panel" role="dialog" aria-modal="true" tabindex="-1">
          <button type="button" class="modal__close" data-close aria-label="Close">✕</button>
          <div class="modal__content"><iframe src="${src}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="Video"></iframe></div>
        </div>`;
      document.body.appendChild(modal);
      requestAnimationFrame(() => Ella.open(modal, trigger));
      modal.addEventListener('ella:close', () => setTimeout(() => modal.remove(), 500), { once: true });
    }
  }
  customElements.define('video-modal-button', VideoModalButton);

  /* ---------- Share ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        if (navigator.share) {
          try {
            await navigator.share(data);
          } catch (e) {
            /* dismissed */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(data.url);
          Ella.toast(Ella.strings.linkCopied);
        } catch (e) {
          window.prompt('', data.url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          const min = Number(this.input.min) || 0;
          const step = Number(this.input.step) || 1;
          const current = Number(this.input.value) || 0;
          const value = button.name === 'plus' ? current + step : Math.max(min, current - step);
          if (value === current) return;
          this.input.value = value;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart ---------- */
  Ella.cart = {
    sectionIds() {
      const ids = [];
      if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
      document.querySelectorAll('cart-page[data-section]').forEach((el) => ids.push(el.dataset.section));
      return ids;
    },

    async add(formData) {
      const ids = this.sectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const response = await fetch(`${Ella.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const json = await response.json();
      if (json.status) throw new Error(json.description || json.message || Ella.strings.error);
      this.render(json.sections);
      return json;
    },

    async change(body) {
      const ids = this.sectionIds();
      const response = await fetch(`${Ella.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: ids, sections_url: window.location.pathname }),
      });
      const json = await response.json();
      if (json.status) throw new Error(json.description || json.message || Ella.strings.error);
      this.render(json.sections);
      return json;
    },

    async updateNote(note) {
      await fetch(`${Ella.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    },

    render(sections) {
      if (!sections) return;
      let count = null;
      if (sections['cart-drawer']) {
        const doc = Ella.parseHTML(sections['cart-drawer']);
        const fresh = doc.getElementById('CartDrawer-Inner');
        const current = document.getElementById('CartDrawer-Inner');
        if (fresh && current) {
          current.replaceWith(fresh);
          count = fresh.dataset.count;
        }
      }
      document.querySelectorAll('cart-page[data-section]').forEach((page) => {
        const html = sections[page.dataset.section];
        if (!html) return;
        const doc = Ella.parseHTML(html);
        const fresh = doc.querySelector('[data-cart-page-inner]');
        const current = page.querySelector('[data-cart-page-inner]');
        if (fresh && current) current.replaceWith(fresh);
        const items = fresh && fresh.querySelectorAll('.cart-item');
        if (count === null && items) {
          count = [...fresh.querySelectorAll('.qty__input[data-line]')].reduce((sum, input) => sum + Number(input.value || 0), 0);
        }
      });
      if (count !== null) this.updateCount(count);
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.dataset.count = count;
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      });
    },
  };

  /* Cart interactions shared by drawer and cart page */
  const cartScope = (el) => el.closest('#CartDrawer, cart-page');

  document.addEventListener('change', async (event) => {
    const target = event.target;
    if (!cartScope(target)) return;

    if (target.matches('.qty__input[data-line]')) {
      const item = target.closest('.cart-item');
      if (item) item.classList.add('is-updating');
      try {
        await Ella.cart.change({ line: Number(target.dataset.line), quantity: Number(target.value) });
      } catch (error) {
        Ella.toast(error.message);
        if (item) item.classList.remove('is-updating');
      }
    }

    if (target.matches('[data-gift-wrap]')) {
      try {
        if (target.checked) {
          const fd = new FormData();
          fd.append('id', target.dataset.giftWrap);
          fd.append('quantity', '1');
          await Ella.cart.add(fd);
        } else {
          await Ella.cart.change({ id: target.dataset.giftWrap, quantity: 0 });
        }
      } catch (error) {
        Ella.toast(error.message);
        target.checked = !target.checked;
      }
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-remove-line]');
    if (!remove || !cartScope(remove)) return;
    const item = remove.closest('.cart-item');
    if (item) item.classList.add('is-updating');
    try {
      await Ella.cart.change({ line: Number(remove.dataset.removeLine), quantity: 0 });
    } catch (error) {
      Ella.toast(error.message);
      if (item) item.classList.remove('is-updating');
    }
  });

  const saveNote = Ella.debounce((value) => Ella.cart.updateNote(value), 600);
  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-cart-note]')) saveNote(event.target.value);
  });

  class CartDrawer extends OverlayElement {}
  customElements.define('cart-drawer', CartDrawer);
  customElements.define('cart-page', class extends HTMLElement {});

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      const drawer = document.getElementById('CartDrawer');
      if (Ella.settings.cartType !== 'drawer' || !drawer) return;
      event.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      const error = this.querySelector('[data-form-error]');
      if (!button || button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      if (error) error.hidden = true;
      try {
        await Ella.cart.add(new FormData(this.form));
        Ella.closeAll(drawer);
        Ella.open(drawer, document.getElementById('CartIcon'));
      } catch (err) {
        if (error) {
          error.textContent = err.message;
          error.hidden = false;
        } else {
          Ella.toast(err.message);
        }
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Variant picker ---------- */
  customElements.define('variant-picker', class extends HTMLElement {});

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const variantsEl = this.querySelector('[data-variants-json]');
      if (!variantsEl) return;
      this.variants = JSON.parse(variantsEl.textContent);
      const inventoryEl = this.querySelector('[data-inventory-json]');
      try {
        this.inventory = inventoryEl ? JSON.parse(inventoryEl.textContent) : {};
      } catch (e) {
        this.inventory = {};
      }
      const idInput = this.querySelector('.js-variant-id');
      this.currentVariant = this.variants.find((v) => idInput && String(v.id) === idInput.value) || this.variants[0];

      this.addEventListener('change', (event) => {
        if (event.target.closest('variant-picker')) this.onOptionChange();
      });

      this.updateAvailability();
      this.updateInventory(this.currentVariant);
      this.setupDelivery();
    }

    getSelectedOptions() {
      return [...this.querySelectorAll('variant-picker [data-option-index]:is(fieldset, select)')].map((el) => {
        if (el.tagName === 'SELECT') return el.value;
        const checked = el.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.getSelectedOptions();
      this.querySelectorAll('variant-picker fieldset').forEach((fieldset) => {
        const label = fieldset.querySelector('[data-selected-value]');
        const checked = fieldset.querySelector('input:checked');
        if (label && checked) label.textContent = checked.value;
      });
      this.updateAvailability();

      const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));
      if (!variant) {
        this.setButtons(false, Ella.strings.unavailable);
        return;
      }
      this.currentVariant = variant;
      this.querySelectorAll('.js-variant-id').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.renderPrice(variant);
      this.setButtons(variant.available, variant.available ? Ella.strings.addToCart : Ella.strings.soldOut);
      this.updateInventory(variant);

      if (!this.hasAttribute('data-no-history') && this.dataset.url) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }
      if (variant.featured_media) {
        const gallery = this.querySelector('product-gallery');
        if (gallery && gallery.goToMedia) gallery.goToMedia(variant.featured_media.id);
      }
      this.dispatchEvent(new CustomEvent('ella:variant-change', { bubbles: true, detail: { variant } }));
    }

    renderPrice(variant) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      this.querySelectorAll('[data-price-wrapper] .price').forEach((price) => {
        price.classList.toggle('price--sale', !!onSale);
        price.innerHTML =
          `<span class="price__current">${Ella.formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare">${Ella.formatMoney(variant.compare_at_price)}</s>` : '');
      });
    }

    setButtons(enabled, label) {
      this.querySelectorAll('[data-add-button], [data-sticky-add]').forEach((button) => {
        button.disabled = !enabled;
        const text = button.querySelector('[data-add-label]');
        if (text) text.textContent = label;
      });
    }

    updateAvailability() {
      const selected = this.getSelectedOptions();
      this.querySelectorAll('variant-picker [data-option-index]:is(fieldset, select)').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        const inputs = group.tagName === 'SELECT' ? [...group.options] : [...group.querySelectorAll('input')];
        inputs.forEach((input) => {
          const available = this.variants.some(
            (v) => v.available && v.options[index] === input.value && v.options.every((opt, i) => i === index || selected[i] == null || opt === selected[i])
          );
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateInventory(variant) {
      const el = this.querySelector('[data-inventory]');
      if (!el || !variant) return;
      const text = el.querySelector('[data-inventory-text]');
      const qty = this.inventory[variant.id];
      const threshold = Number(el.dataset.threshold) || 5;
      el.classList.remove('is-low', 'is-hidden');
      if (!variant.available) {
        el.classList.add('is-hidden');
      } else if (qty != null && qty > 0 && qty <= threshold) {
        el.classList.add('is-low');
        text.textContent = el.dataset.lowText.replace('[count]', qty);
      } else if (el.dataset.showIn === 'true') {
        text.textContent = el.dataset.inText;
      } else {
        el.classList.add('is-hidden');
      }
    }

    setupDelivery() {
      const el = this.querySelector('[data-delivery]');
      if (!el) return;
      const addDays = (count) => {
        const date = new Date();
        let added = 0;
        while (added < count) {
          date.setDate(date.getDate() + 1);
          if (date.getDay() !== 5) added += 1;
        }
        return date;
      };
      let formatter;
      try {
        formatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      } catch (e) {
        formatter = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
      }
      const start = formatter.format(addDays(Number(el.dataset.min) || 0));
      const end = formatter.format(addDays(Number(el.dataset.max) || 0));
      el.textContent = (el.dataset.template || '').replace('[start]', start).replace('[end]', end);
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.dots = [...this.querySelectorAll('[data-gallery-dots] [data-index]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumbs] [data-index]')];
      this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.syncActive()), { passive: true });
      [...this.dots, ...this.thumbs].forEach((btn) => btn.addEventListener('click', () => this.scrollToIndex(Number(btn.dataset.index))));
      this.querySelectorAll('[data-zoom]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const box = this.closest('product-info');
          const lightbox = box && box.querySelector('ella-lightbox');
          if (lightbox) lightbox.openAt(Number(btn.dataset.zoom), btn);
        });
      });
    }

    get slides() {
      return [...this.track.querySelectorAll('.gallery__slide')];
    }

    isHorizontal() {
      return this.track.scrollWidth > this.track.clientWidth + 5;
    }

    syncActive() {
      if (!this.isHorizontal()) return;
      const index = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
      this.setActive(index);
    }

    setActive(index) {
      [this.dots, this.thumbs].forEach((list) => list.forEach((el, i) => el.classList.toggle('is-active', i === index)));
    }

    scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide) return;
      const trackRect = this.track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const delta = isRTL() ? slideRect.right - trackRect.right : slideRect.left - trackRect.left;
      this.track.scrollBy({ left: delta, behavior: 'smooth' });
      this.setActive(index);
    }

    goToMedia(mediaId) {
      const slides = this.slides;
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      if (this.isHorizontal()) {
        this.scrollToIndex(index);
      } else {
        this.track.prepend(slides[index]);
        const top = this.getBoundingClientRect().top;
        if (top < 0) window.scrollTo({ top: window.scrollY + top - 120, behavior: 'smooth' });
      }
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class EllaLightbox extends OverlayElement {
    openAt(index, trigger) {
      this.open(trigger);
      const item = this.querySelector(`.lightbox__item[data-index="${index}"]`);
      if (item) requestAnimationFrame(() => item.scrollIntoView({ block: 'start' }));
    }
  }
  customElements.define('ella-lightbox', EllaLightbox);

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('product-info');
      this.mainButton = this.info && this.info.querySelector('.product__buy [data-add-button]');
      if (!this.mainButton || !('IntersectionObserver' in window)) return;
      const button = this.querySelector('[data-sticky-add]');
      if (button) button.addEventListener('click', () => this.mainButton.click());
      this.observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('has-sticky-atc', show);
      });
      this.observer.observe(this.mainButton);
    }
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Quick view ---------- */
  class QuickViewModal extends OverlayElement {
    async load(url, trigger) {
      const content = this.querySelector('[data-modal-content]');
      content.innerHTML = '<div class="modal__loader"><span class="loader-sparkle"><svg class="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c.6 6.6 5.4 11.4 12 12-6.6.6-11.4 5.4-12 12-.6-6.6-5.4-11.4-12-12C6.6 11.4 11.4 6.6 12 0z"/></svg></span></div>';
      this.open(trigger);
      try {
        const target = new URL(url, window.location.origin);
        target.search = '';
        target.searchParams.set('section_id', 'quick-view');
        const response = await fetch(target.toString());
        const doc = Ella.parseHTML(await response.text());
        const product = doc.querySelector('.quick-view__product');
        if (!product) throw new Error();
        content.innerHTML = '';
        content.appendChild(product);
        Ella.reveal(content);
      } catch (error) {
        window.location.href = url;
      }
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);

  /* ---------- Search overlay & predictive search ---------- */
  class SearchOverlay extends OverlayElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.enabled = this.dataset.predictive === 'true';
      if (this.input) this.input.setAttribute('data-autofocus', '');
      if (!this.input || !this.enabled) return;
      this.input.addEventListener('input', Ella.debounce(() => this.search(), 260));
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.innerHTML = '';
        this.classList.remove('has-results');
        return;
      }
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article,query',
        'resources[limit]': '6',
        section_id: 'predictive-search',
      });
      try {
        const response = await fetch(`${Ella.routes.predictiveSearch}?${params}`, { signal: this.abort.signal });
        const doc = Ella.parseHTML(await response.text());
        const section = doc.getElementById('shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
        this.classList.add('has-results');
      } catch (error) {
        /* aborted or offline */
      }
    }
  }
  customElements.define('search-overlay', SearchOverlay);

  /* ---------- Facets (filters & sorting) ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.drawer = this.querySelector('[data-facets-drawer]');
      const apply = Ella.debounce(() => this.applyForm(), 450);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-facet-input]')) apply();
      });
      document.addEventListener('change', (event) => {
        if (event.target.matches(`select[form="FacetsForm-${this.sectionId}"]`) && !this.contains(event.target)) apply();
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          this.apply(new URL(link.href, window.location.origin).searchParams.toString());
        }
        if (event.target.closest('[data-facets-open]') && this.drawer) Ella.open(this.drawer, event.target.closest('[data-facets-open]'));
      });
      this.onPopState = () => this.apply(window.location.search.slice(1), false);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    applyForm() {
      const form = this.querySelector('[data-facets-form]');
      const data = form ? new FormData(form) : new FormData();
      const sort = document.querySelector(`select[form="FacetsForm-${this.sectionId}"]`);
      if (sort && !form) data.set('sort_by', sort.value);
      const params = new URLSearchParams();
      data.forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      this.apply(params.toString());
    }

    async apply(params, push = true) {
      const results = document.getElementById(`ResultsGrid-${this.sectionId}`);
      if (results) results.classList.add('is-loading');
      const openIds = [...this.querySelectorAll('details.facet[open]')].map((d) => d.id);
      try {
        const separator = params ? '&' : '';
        const response = await fetch(`${window.location.pathname}?${params}${separator}section_id=${this.sectionId}`);
        const doc = Ella.parseHTML(await response.text());

        const freshResults = doc.getElementById(`ResultsGrid-${this.sectionId}`);
        if (results && freshResults) results.innerHTML = freshResults.innerHTML;

        this.querySelectorAll('[data-facet-update]').forEach((el) => {
          const fresh = doc.getElementById(el.id);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
        openIds.forEach((id) => {
          const details = document.getElementById(id);
          if (details) details.open = true;
        });
        if (push) window.history.pushState({}, '', `${window.location.pathname}${params ? `?${params}` : ''}`);
        if (results) Ella.reveal(results);
      } catch (error) {
        window.location.search = params;
      } finally {
        if (results) results.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* Load more */
  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-load-more]');
    if (!link) return;
    const results = link.closest('[id^="ResultsGrid-"]');
    if (!results) return;
    event.preventDefault();
    const sectionId = results.id.replace('ResultsGrid-', '');
    link.classList.add('is-loading');
    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      const doc = Ella.parseHTML(await (await fetch(url)).text());
      const freshResults = doc.getElementById(results.id);
      const grid = results.querySelector('[data-product-grid]');
      const freshGrid = freshResults && freshResults.querySelector('[data-product-grid]');
      if (grid && freshGrid) {
        const items = [...freshGrid.children];
        items.forEach((item) => grid.appendChild(item));
        Ella.reveal(grid);
      }
      const wrap = results.querySelector('[data-load-more-wrap]');
      const freshWrap = freshResults && freshResults.querySelector('[data-load-more-wrap]');
      if (wrap) {
        if (freshWrap) wrap.replaceWith(freshWrap);
        else wrap.remove();
      }
    } catch (error) {
      window.location.href = link.href;
    }
  });

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      const load = async () => {
        this.loaded = true;
        try {
          const doc = Ella.parseHTML(await (await fetch(this.dataset.url)).text());
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            Ella.reveal(this);
          }
        } catch (error) {
          /* ignore */
        }
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Newsletter popup ---------- */
  class NewsletterPopup extends OverlayElement {
    connectedCallback() {
      const key = 'ella-popup-dismissed';
      const days = Number(this.dataset.frequency) || 14;
      const posted = /customer_posted=true/.test(window.location.search) && window.location.hash === '#NewsletterPopupForm';
      this.addEventListener('ella:close', () => {
        try {
          localStorage.setItem(key, String(Date.now()));
        } catch (e) {
          /* storage blocked */
        }
      });
      if (posted) {
        Ella.whenReady(() => this.open());
        return;
      }
      if (designMode) return;
      let last = 0;
      try {
        last = Number(localStorage.getItem(key)) || 0;
      } catch (e) {
        last = 0;
      }
      if (Date.now() - last < days * 86400000) return;
      setTimeout(() => {
        if (!overlayStack.length) this.open();
      }, (Number(this.dataset.delay) || 0) * 1000);
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);

  /* ---------- Global triggers ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target;

    const closer = target.closest('[data-close]');
    if (closer) {
      const host = closer.closest(OVERLAY_SELECTOR);
      if (host && host.classList.contains('is-open')) {
        Ella.close(host);
        return;
      }
    }

    const menuOpen = target.closest('[data-menu-open]');
    if (menuOpen) {
      Ella.open(document.getElementById('MenuDrawer'), menuOpen);
      return;
    }

    const searchOpen = target.closest('[data-search-open]');
    if (searchOpen) {
      Ella.open(document.getElementById('SearchOverlay'), searchOpen);
      return;
    }

    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen && document.getElementById('CartDrawer')) {
      event.preventDefault();
      Ella.closeAll();
      Ella.open(document.getElementById('CartDrawer'), cartOpen);
      return;
    }

    const quick = target.closest('[data-quick-view]');
    if (quick) {
      event.preventDefault();
      const modal = document.getElementById('QuickView');
      if (modal && modal.load) {
        Ella.closeAll();
        modal.load(quick.dataset.quickView, quick);
      } else {
        window.location.href = quick.dataset.quickView;
      }
      return;
    }

    const modalOpen = target.closest('[data-modal-open]');
    if (modalOpen) {
      Ella.open(document.getElementById(modalOpen.dataset.modalOpen), modalOpen);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]') && event.target.form) event.target.form.submit();
  });

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', (event) => {
    Ella.reveal(event.target);
    Ella.parallax(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const host = event.target.closest('ella-slideshow, ella-fader, announcement-bar, look-book, ella-tabs');
    if (host && host.selectBlock) host.selectBlock(event.target);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const host = event.target.closest('ella-slideshow');
    if (host && host.deselectBlock) host.deselectBlock();
  });

  /* ---------- Init ---------- */
  Ella.whenReady(() => {
    Ella.reveal();
    Ella.parallax();
  });
})();
